import { useCallback, useEffect, useRef, useState } from 'react';
import { Skeleton, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { DailyBite, TodaySummary } from '@nelnela/contracts';
import { useStudy } from '../app/study';
import { api } from '../lib/api';
import { speak, ttsCalmLine, ttsPlayer } from '../lib/tts-player';
import { useOnline } from '../lib/sw';
import {
  advancePlayAll,
  canPlayAll,
  clearRun,
  loadRun,
  saveRun,
  shouldAdvance,
  startPlayAll,
  stopPlayAll,
  tileHref,
  IDLE as PLAY_ALL_IDLE,
  type PlayAllState,
} from '../lib/play-all';
import { InfoStrip } from '../components/InfoStrip';
import { GoalRing } from '../components/today/GoalRing';
import { Tile } from '../components/today/Tile';
import { BiteTiles, EveningClose } from '../components/today/BiteTiles';
import { PatternSheet } from '../components/today/PatternSheet';
import { isEvening, overMinutes } from '../components/today/plan';
import { MarkCelebration } from '../components/brand/Mark';
import {
  formatTodayDate,
  localDateOf,
  relativeDay,
  youtubeStill,
} from '../components/today/format';
import { IconPlay } from '../components/watch/icons';

/**
 * F1 Today — the frame around the daily bite (design `nelnela Today.dc.html`, frames `2b`
 * mobile and `2a` desktop).
 *
 * M5 builds everything the app can actually say today: the date, the level pill, the line you
 * understood yesterday, the goal ring, the streak, what to carry on watching, and what you
 * caught. M6a fills the tile area with the bite itself. That division is deliberate — a screen
 * of placeholder tiles would be worse on day one than a short screen that is entirely true.
 *
 * The rule that shapes every branch below: **a fact, never debt**. A zero streak renders no
 * line rather than "0-day streak"; an unmeasured level renders no pill rather than "~1"; no
 * bite yet renders a plain sentence rather than an empty scaffold. There is no copy anywhere
 * about what you missed.
 */
export function Today() {
  const { me } = useStudy();
  // `?? false` matches Watch.tsx: the hook is typed `boolean | undefined` and mobile is the
  // baseline, so an unknown viewport renders the phone layout rather than nothing.
  const desktop =
    useMediaQuery('(min-width: 62em)', false, { getInitialValueInEffect: false }) ?? false;
  const online = useOnline();
  const profile = me.profiles[0] ?? null;

  const q = useQuery({
    queryKey: ['today', profile?.id],
    queryFn: () => api.todaySummary(profile!.id),
    enabled: !!profile,
  });
  /**
   * The day's plan. A SEPARATE query from the summary on purpose: composing a bite writes a row
   * and can take a moment, where the summary is a pure read that Today must be able to paint
   * without it. So the chrome — the date, the greeting, the ring — appears immediately and the
   * tiles land underneath it, rather than the whole screen waiting on the slower of the two.
   *
   * Deliberately NOT service-worker cached, unlike the summary: the response is per-DAY and
   * URL-keyed, so a cached copy would serve yesterday's plan (with its tiles already marked
   * complete) on the first open of a new day.
   */
  const biteQ = useQuery({
    queryKey: ['daily-bite', profile?.id],
    queryFn: () => api.dailyBite(profile!.id),
    enabled: !!profile,
  });

  return (
    <div style={{ minHeight: '100%' }}>
      {!online && (
        <InfoStrip>Offline — Today, Words and saved audio work; Watch needs the network.</InfoStrip>
      )}
      <div
        style={
          desktop
            ? {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '0 0 16px 56px',
                minHeight: '100dvh',
              }
            : { padding: '13px 16px 0' }
        }
      >
        <div style={desktop ? { width: 820, maxWidth: '100%' } : undefined}>
          {q.isPending ? (
            <TodaySkeleton desktop={desktop} />
          ) : q.error || !q.data ? (
            <Text fz={13} c="var(--nn-muted)" py="lg">
              Today didn’t load. It’ll be here when the connection is.
            </Text>
          ) : (
            <>
              {/* M10-iii: the wizard is "offered at set-up, re-runnable from Me". This is the
                  set-up half — shown only while there is no estimate at all, so it disappears the
                  moment placement runs (or the learner sets a level by hand) and never nags. */}
              {profile && profile.levelEstimate === null && <PlacementOffer />}
              <TodayBody
                summary={q.data}
                bite={biteQ.data ?? null}
                biteLoading={biteQ.isPending}
                profileId={profile?.id ?? null}
                timezone={profile?.timezone ?? 'UTC'}
                desktop={desktop}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TodayBody({
  summary,
  bite,
  biteLoading,
  profileId,
  timezone,
  desktop,
}: {
  summary: TodaySummary;
  bite: DailyBite | null;
  biteLoading: boolean;
  profileId: string | null;
  timezone: string;
  desktop: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { entitled } = useStudy();
  const evening = isEvening(summary);
  const [pattern, setPattern] = useState<DailyBite['plan'][number] | null>(null);
  const [play, setPlayState] = useState<PlayAllState>(PLAY_ALL_IDLE);
  // Every change to the run is mirrored into sessionStorage: the next tile navigates away from
  // this component, so React state alone would forget the run after one tile.
  const setPlay = useCallback(
    (next: PlayAllState) => {
      setPlayState(next);
      if (bite) saveRun(bite.id, next);
      else clearRun();
    },
    [bite],
  );

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['daily-bite', profileId] });
  }, [qc, profileId]);

  const dismiss = useMutation({
    mutationFn: (planIdx: number) => api.dailyBiteDismiss(bite!.id, planIdx),
    onSuccess: invalidate,
  });
  const reason = useMutation({
    mutationFn: (v: {
      activityId: string;
      reason: 'too_hard' | 'too_easy' | 'no_time' | 'bored';
    }) => api.activityReason(v.activityId, v.reason),
  });
  const more = useMutation({
    mutationFn: () => api.dailyBiteRebuild(profileId!, 'extend'),
    onSuccess: invalidate,
  });

  /**
   * "Play all" opens the first tile and remembers the rest. The queue lives here rather than in
   * a route because the run has to survive the navigations it performs — every tile is a real
   * screen, and coming back to Today is how one tile ends and the next begins.
   */
  const playAll = (): void => {
    if (!bite) return;
    const next = startPlayAll(bite.plan);
    setPlay(next);
    const tile = bite.plan.find((t) => t.idx === next.current);
    const href = tile ? tileHref(bite, tile) : null;
    if (href) navigate(href);
    else setPlay(stopPlayAll());
  };

  /**
   * Coming back to Today mid-run.
   *
   * Fires once per plan, and only when the tile that was open has actually FINISHED — someone
   * who opened Watch, watched a minute and came back has not finished it, and being marched on
   * to the review would be the app deciding for them. The guard ref stops a refetch of the plan
   * (which happens on every dismiss and every rebuild) from advancing the run a second time.
   */
  const advancedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!bite) return;
    const stored = loadRun(bite.id);
    if (!stored) {
      if (play.status === 'running') setPlayState(PLAY_ALL_IDLE);
      return;
    }
    if (!shouldAdvance(stored, bite.plan)) {
      setPlayState(stored);
      return;
    }
    const key = `${bite.id}:${stored.current}`;
    if (advancedFor.current === key) return;
    advancedFor.current = key;

    const next = advancePlayAll(stored);
    setPlayState(next);
    saveRun(bite.id, next);
    const tile = bite.plan.find((t) => t.idx === next.current);
    const href = tile ? tileHref(bite, tile) : null;
    if (href) navigate(href);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bite, navigate]);

  return (
    <>
      <HeaderRow summary={summary} desktop={desktop} />
      {!evening && summary.yesterdayLine && (
        <YesterdayRow line={summary.yesterdayLine} desktop={desktop} />
      )}
      <RingRow
        summary={summary}
        desktop={desktop}
        evening={evening}
        canPlay={!evening && !!bite && entitled && canPlayAll(bite.plan)}
        onPlayAll={playAll}
      />

      <div style={{ marginTop: evening ? 16 : desktop ? 20 : 9 }}>
        {biteLoading ? (
          <TilesSkeleton desktop={desktop} />
        ) : !bite || bite.plan.length === 0 ? (
          <EmptyPlan entitled={entitled} summary={summary} timezone={timezone} desktop={desktop} />
        ) : (
          <BiteTiles
            bite={bite}
            summary={summary}
            evening={evening}
            desktop={desktop}
            daysUntilDue={null}
            onDismiss={(idx) => dismiss.mutate(idx)}
            onReason={(idx, r) => {
              const t = bite.plan.find((x) => x.idx === idx);
              if (t?.abandonedActivityId)
                reason.mutate({ activityId: t.abandonedActivityId, reason: r });
            }}
            onPattern={setPattern}
          />
        )}
      </div>

      {/* The thin-plan door (2026-09-03 walkthrough): a one-tile morning left the screen mostly
          void with no way to ask for more — the extend affordance only existed in the evening
          state. Same mutation, offered quietly whenever the plan is thin and the day is young. */}
      {!evening && entitled && bite && bite.plan.filter((t) => !t.dismissed).length <= 2 && (
        <UnstyledButton
          onClick={() => more.mutate()}
          disabled={more.isPending}
          style={{
            marginTop: 10,
            fontSize: 11.5,
            color: 'var(--nn-link)',
            opacity: more.isPending ? 0.5 : 1,
          }}
        >
          a light plan today — deal me more →
        </UnstyledButton>
      )}

      {evening && bite && <EveningClose onMore={() => more.mutate()} busy={more.isPending} />}

      {!evening && summary.caughtYesterday.length > 0 && (
        <CaughtChips items={summary.caughtYesterday} desktop={desktop} />
      )}

      {bite && (
        <PatternSheet
          opened={pattern !== null}
          onClose={() => setPattern(null)}
          tile={pattern}
          biteId={bite.id}
        />
      )}
    </>
  );
}

/**
 * What stands where the bite would be when there is no plan at all (design `2h`).
 *
 * Three different nothings, and they are not the same sentence. An unentitled learner is told
 * their trial ended and where to go; a learner with nothing watched yet is pointed at the
 * library; a learner who has watched something gets the thing they were watching. None of them
 * gets a scaffold of empty tiles.
 */
function EmptyPlan({
  entitled,
  summary,
  timezone,
  desktop,
}: {
  entitled: boolean;
  summary: TodaySummary;
  timezone: string;
  desktop: boolean;
}) {
  if (!entitled) {
    return (
      <Tile title="Your trial has ended.">
        <Link to="/me" style={{ fontSize: 12, color: 'var(--nn-link)' }}>
          Account
        </Link>
      </Tile>
    );
  }
  if (summary.continueWatching) {
    return (
      <HeroCard
        watching={summary.continueWatching}
        timezone={timezone}
        today={summary.date}
        desktop={desktop}
      />
    );
  }
  return (
    <Tile title="Nothing planned yet — start anywhere.">
      <div style={{ display: 'flex', gap: 14, marginTop: 2 }}>
        <Link to="/watch" style={{ fontSize: 12, color: 'var(--nn-link)' }}>
          Browse the library
        </Link>
        <Link to="/read" style={{ fontSize: 12, color: 'var(--nn-link)' }}>
          Read a story
        </Link>
      </div>
    </Tile>
  );
}

/**
 * "Find your level" — the set-up half of the wizard's offer (M10-iii).
 *
 * One line and one link, above the day rather than instead of it: a learner with no estimate can
 * still study, and the app already works from measured evidence — `level_estimate` is a PRIOR, and
 * MemoryState decides. So this is an offer, not a gate, and it removes itself the moment there is
 * an estimate to have.
 */
function PlacementOffer() {
  return (
    <div
      style={{
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 10,
        padding: '11px 13px',
        marginBottom: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
      }}
    >
      <span style={{ fontSize: 12.5, color: 'var(--nn-muted)' }}>
        Practice starts at your edge — we don’t know where yours is yet.
      </span>
      <Link
        to="/placement"
        style={{ fontSize: 12.5, color: 'var(--nn-accent-text)', flexShrink: 0 }}
      >
        Find your level →
      </Link>
    </div>
  );
}

function TilesSkeleton({ desktop }: { desktop: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <Skeleton height={desktop ? 150 : 190} radius={10} />
      <Skeleton height={64} radius={10} />
      <Skeleton height={64} radius={10} />
    </div>
  );
}

/** Date on the left; the level pill on the right, or nothing at all when there is no level. */
function HeaderRow({ summary, desktop }: { summary: TodaySummary; desktop: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span style={{ fontSize: desktop ? 13 : 12, color: 'var(--nn-muted)' }}>
        {formatTodayDate(summary.date)}
      </span>
      <span style={{ flex: 1 }} />
      {summary.levelEstimate !== null && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 10px',
            borderRadius: 999,
            border: '1px solid var(--nn-hairline)',
            fontSize: 11,
            color: 'var(--nn-muted)',
          }}
        >
          <span>~{summary.levelEstimate}</span>
          <span style={{ color: 'var(--nn-faint)' }}>level</span>
          {/* M6a: the delta is the point — coverage moving, not a score. Until there is a
              weekly snapshot to diff, the span is simply absent. */}
          {summary.levelDeltaWeek !== null && (
            <span style={{ color: 'var(--nn-accent-text)' }}>
              {summary.levelDeltaWeek > 0 ? '+' : ''}
              {summary.levelDeltaWeek} this week
            </span>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * The greeting: not "Good morning" but something you understood yesterday. Tapping plays it —
 * the saved audio when there is one, otherwise synthesized on the tap itself.
 */
function YesterdayRow({
  line,
  desktop,
}: {
  line: NonNullable<TodaySummary['yesterdayLine']>;
  desktop: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const play = async () => {
    const track = { title: line.text, subtitle: 'from yesterday', key: 'today:yesterday' };
    setError(null);
    try {
      // Candidate 2 has no audio yet, so the tap synthesizes — which is the entitled,
      // network-dependent path, and the one that can fail. `speak` rethrows and the now-playing
      // bar just disappears, so without this the learner taps again and again with no answer.
      // (Words and Watch both surface `ttsCalmLine` this way; this row was the odd one out.)
      if (line.audio) await ttsPlayer.play({ url: line.audio.url, ...track });
      else await speak(line.text, track);
    } catch (err) {
      setError(ttsCalmLine(err));
    }
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: desktop ? 11 : 10,
        marginTop: desktop ? 16 : 10,
      }}
    >
      <UnstyledButton
        onClick={() => void play()}
        aria-label="Play yesterday’s line"
        style={{
          width: desktop ? 28 : 26,
          height: desktop ? 28 : 26,
          borderRadius: 999,
          background: 'color-mix(in srgb, var(--nn-accent) 13%, transparent)',
          border: '1px solid color-mix(in srgb, var(--nn-accent) 30%, transparent)',
          color: 'var(--nn-accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ display: 'flex', transform: `scale(${(desktop ? 11 : 10) / 22})` }}>
          <IconPlay />
        </span>
      </UnstyledButton>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span
          style={{
            fontSize: desktop ? 10.5 : 10,
            color: 'var(--nn-faint)',
            letterSpacing: '.05em',
          }}
        >
          {desktop ? 'FROM YESTERDAY — TAP TO HEAR IT AGAIN' : 'FROM YESTERDAY'}
        </span>
        <span
          lang="ka"
          style={{
            fontFamily: "'Noto Serif Georgian', serif",
            fontSize: desktop ? 16.5 : 14.5,
            color: 'var(--nn-text-strong)',
            ...(desktop
              ? {}
              : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
          }}
        >
          {line.text}
          {/* Desktop has the width for the translation inline; mobile keeps one clean line. */}
          {desktop && line.translation && (
            <span
              lang="en"
              style={{
                fontSize: 12,
                color: 'var(--nn-muted)',
                fontFamily: "'Noto Sans Georgian', sans-serif",
                marginLeft: 7,
              }}
            >
              {line.translation}
            </span>
          )}
        </span>
        {error && (
          <span style={{ fontSize: 11, color: 'var(--nn-muted)' }} role="status">
            {error}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * The ring, the minutes line, and the streak — stated as a fact, never rewarded.
 *
 * Two states (`2b` and `2c`). Before the goal it counts: a numeral in the ring and "{n} of 15
 * min today". Once the goal is met the ring fills, the numeral goes, the mark plays once inside
 * it, and the line becomes "Today's goal +8 min" — the overflow in ember, bigger than the label,
 * because past the goal the surplus is the fact and the goal is only its unit. The ring itself
 * never changes: it does not lap, and a second lap would turn a goal into a score.
 */
function RingRow({
  summary,
  desktop,
  evening,
  canPlay,
  onPlayAll,
}: {
  summary: TodaySummary;
  desktop: boolean;
  evening: boolean;
  canPlay: boolean;
  onPlayAll: () => void;
}) {
  const over = overMinutes(summary);
  const size = desktop ? 56 : 48;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: desktop ? 14 : 13,
        marginTop: desktop ? 18 : 10,
      }}
    >
      <span
        style={{
          position: 'relative',
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <GoalRing
          minutesDone={summary.minutesDone}
          goalMinutes={summary.goalMinutes}
          size={size}
          complete={evening}
        />
        {/* Centred INSIDE the ring, where the numeral was. It plays once — the canvas loops it,
            but a mark that kept celebrating all evening would stop meaning anything. */}
        {evening && (
          <MarkCelebration size={30} style={{ position: 'absolute', left: (size - 30) / 2 }} />
        )}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: desktop ? 2 : 1 }}>
        {evening ? (
          <span style={{ fontSize: desktop ? 14.5 : 14, color: 'var(--nn-text-strong)' }}>
            Today’s goal{' '}
            {over !== null && (
              <span style={{ color: 'var(--nn-accent-text)', fontWeight: 700, fontSize: 15 }}>
                +{over} min
              </span>
            )}
          </span>
        ) : (
          <span style={{ fontSize: desktop ? 14.5 : 13, color: 'var(--nn-text-strong)' }}>
            {summary.minutesDone} of {summary.goalMinutes} min today
          </span>
        )}
        {/* Never "0-day streak": a streak that does not exist is not a number, it is nothing. */}
        {summary.streakDays > 0 && (
          <span style={{ fontSize: desktop ? 12 : 11.5, color: 'var(--nn-muted)' }}>
            {summary.streakDays}-day streak
          </span>
        )}
      </span>
      {canPlay && (
        <>
          <span style={{ flex: 1 }} />
          {/* No minutes on the pill (2026-09-03). It used to name the GOAL, defended as "never
              promise a longer day than agreed" — but on a thin-plan morning it read as the PLAN's
              length and overstated a 1-minute day by fourteen. The ring one inch left already
              says "{n} of 15 min today"; the pill repeating a number, either number, is what made
              it lie in one direction or the other. */}
          <UnstyledButton
            onClick={onPlayAll}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 12px',
              borderRadius: 999,
              background: 'color-mix(in srgb, var(--nn-accent) 13%, transparent)',
              border: '1px solid color-mix(in srgb, var(--nn-accent) 30%, transparent)',
              color: 'var(--nn-accent-text)',
              fontSize: 11.5,
              flexShrink: 0,
            }}
          >
            <span style={{ display: 'flex', transform: 'scale(.45)', width: 10, height: 10 }}>
              <IconPlay />
            </span>
            Play all
          </UnstyledButton>
        </>
      )}
    </div>
  );
}

/**
 * Continue watching. The still is the thumbnail the YouTube embed itself paints, fetched from
 * the player's own host — never rehosted, never a per-timestamp frame (not achievable for an
 * embed). Offline, where nothing cross-origin is cached, `onError` leaves the plain dark well,
 * which is exactly what the DoD means by "hero-as-well".
 */
function HeroCard({
  watching,
  timezone,
  today,
  desktop,
}: {
  watching: NonNullable<TodaySummary['continueWatching']>;
  timezone: string;
  today: string;
  desktop: boolean;
}) {
  const [stillFailed, setStillFailed] = useState(false);
  const still = youtubeStill(watching.provider, watching.externalId);
  const when = relativeDay(localDateOf(watching.lastWatchedAt, timezone), today);
  return (
    <Link
      to={`/watch/${watching.videoId}`}
      style={{
        position: 'relative',
        display: 'block',
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 10,
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        padding: desktop ? '12px 12px 16px' : 0,
      }}
    >
      <div
        style={{
          position: 'relative',
          height: desktop ? 118 : 104,
          borderRadius: desktop ? 7 : 0,
          overflow: 'hidden',
          // Video wells stay dark in both schemes — a white box behind a still is a flash.
          background: 'var(--mantine-color-dark-9)',
        }}
      >
        {still && !stillFailed && (
          <img
            src={still}
            alt=""
            onError={() => setStillFailed(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
      </div>
      <div style={desktop ? { marginTop: 11 } : { padding: '9px 13px 11px' }}>
        <div
          style={{ fontSize: desktop ? 14.5 : 14, color: 'var(--nn-text-strong)', lineHeight: 1.4 }}
        >
          Continue: {watching.title ?? 'your last video'}
        </div>
        <div style={{ marginTop: 3, fontSize: desktop ? 12 : 11.5, color: 'var(--nn-muted)' }}>
          {watching.channelName ? `${watching.channelName} · ` : ''}last watched {when}
        </div>
      </div>
    </Link>
  );
}

/** Ember chips: what the app noticed you capture yesterday. Tapping one opens it in Words. */
function CaughtChips({
  items,
  desktop,
}: {
  items: TodaySummary['caughtYesterday'];
  desktop: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: desktop ? 8 : 6,
        marginTop: desktop ? 16 : 9,
      }}
    >
      <span style={{ fontSize: desktop ? 11 : 10.5, color: 'var(--nn-faint)' }}>
        yesterday you caught
      </span>
      {items.map((c) => (
        <Link
          key={c.itemId}
          to={`/words/item/${c.itemId}`}
          lang="ka"
          style={{
            padding: desktop ? '4px 11px' : '3px 10px',
            borderRadius: 999,
            background: 'color-mix(in srgb, var(--nn-accent) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--nn-accent) 22%, transparent)',
            fontSize: desktop ? 12 : 11.5,
            color: 'var(--nn-accent-text)',
            textDecoration: 'none',
          }}
        >
          {c.display}
        </Link>
      ))}
      {desktop && (
        <span style={{ fontSize: 11, color: 'var(--nn-faint)' }}>· tap any to review in Words</span>
      )}
    </div>
  );
}

/** Skeletons, never spinners (design README §6). The shape of the screen, not a spinning disc. */
function TodaySkeleton({ desktop }: { desktop: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: desktop ? 18 : 10 }}>
      <Skeleton height={14} width="45%" radius="sm" />
      <Skeleton height={desktop ? 22 : 20} width="70%" radius="sm" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
        <Skeleton height={desktop ? 56 : 48} circle />
        <Skeleton height={16} width={160} radius="sm" />
      </div>
      <Skeleton height={desktop ? 150 : 140} radius={10} />
    </div>
  );
}
